
import type { Permission } from "../repositories/customPackage";

const DISPLAY_NAMES: { [key: string]: string } = {
  "aims.access": "AIMS Access",
  "headless.access": "API Access",
  "pitches.add": "Pitch Limit",
  "roaster.add": "Roster Limit",
  "email.access": "Email Access",
};

export const getPermissionDisplayName = (permissionName: string): string =>
  DISPLAY_NAMES[permissionName] || permissionName;


const NUMERIC_LIMIT_PERMISSIONS = new Set(["pitches.add", "roaster.add"]);

export type PermissionControl = "numeric" | "toggle";

export const getPermissionControl = (permissionName: string): PermissionControl =>
  NUMERIC_LIMIT_PERMISSIONS.has(permissionName) ? "numeric" : "toggle";

export interface PermissionFieldValue {
  access: boolean;
  limit: string;
  unlimited: boolean;
}

export type PermissionValues = Record<number, PermissionFieldValue>;

export const defaultFieldValue = (): PermissionFieldValue => ({
  access: false,
  limit: "",
  unlimited: false,
});

export const buildDefaultValues = (permissions: Permission[]): PermissionValues => {
  const values: PermissionValues = {};
  for (const permission of permissions) {
    values[permission.id] = defaultFieldValue();
  }
  return values;
};


export const resolveLimit = (
  permission: Permission,
  value: PermissionFieldValue
): number | null | undefined => {
  if (getPermissionControl(permission.name) === "numeric") {
    if (value.unlimited) return -1;
    const parsed = parseInt(value.limit);
    if (!Number.isInteger(parsed)) return undefined; // invalid → caller flags it
    return parsed;
  }
  return value.access ? null : 0;
};

export const isFieldInvalid = (
  permission: Permission,
  value: PermissionFieldValue
): boolean => {
  if (getPermissionControl(permission.name) !== "numeric") return false;
  if (value.unlimited) return false;
  return !Number.isInteger(parseInt(value.limit));
};

export const valuesFromGrants = (
  permissions: Permission[],
  grants: Array<{ permissionId: number; limit: number | null }>
): PermissionValues => {
  const byId = new Map(grants.map((g) => [g.permissionId, g.limit]));
  const values: PermissionValues = {};
  for (const permission of permissions) {
    if (!byId.has(permission.id)) {
      values[permission.id] = defaultFieldValue();
      continue;
    }
    const limit = byId.get(permission.id)!;
    if (getPermissionControl(permission.name) === "numeric") {
      if (limit === -1 || limit === null) {
        values[permission.id] = { access: false, limit: "", unlimited: true };
      } else {
        values[permission.id] = { access: false, limit: String(limit), unlimited: false };
      }
    } else {
      values[permission.id] = { access: limit === null, limit: "", unlimited: false };
    }
  }
  return values;
};
